import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Modal, Row, Col, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const ROLE_LABELS = { admin: 'Admin', campaign_manager: 'Campaign Manager' };

export default function UsersListPage() {
  const toast = useToast();
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (q) params.set('q', q);
      if (roleFilter) params.set('role', roleFilter);
      if (activeFilter !== '') params.set('is_active', activeFilter);
      const res = await adminApi.get(`/admin/users?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, q ? 220 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, roleFilter, activeFilter, page]);

  async function toggleActive(u) {
    try {
      const endpoint = u.is_active ? 'disable' : 'enable';
      await adminApi.post(`/admin/users/${u.id}/${endpoint}`);
      toast.success(`${u.email} ${u.is_active ? 'disabled' : 'enabled'}.`);
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  async function doDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/users/${pendingDelete.id}`);
      toast.success(`Deleted ${pendingDelete.email}.`);
      setPendingDelete(null);
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage administrators and campaign managers. Assign campaign managers to candidates from the candidate edit screen."
        actions={
          <Button as={Link} to="/admin/users/new" variant="primary" data-testid="users-new-button">
            <i className="bi bi-person-plus me-1" /> New user
          </Button>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={5}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Search</Form.Label>
                <Form.Control
                  placeholder="Name, email, or phone"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setPage(1); }}
                  data-testid="users-search-input"
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Role</Form.Label>
                <Form.Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }} data-testid="users-role-filter">
                  <option value="">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="campaign_manager">Campaign Manager</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Status</Form.Label>
                <Form.Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }} data-testid="users-active-filter">
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Disabled</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-people" />
              <div>No users match your filters.</div>
              <Button as={Link} to="/admin/users/new" variant="primary" className="mt-2">
                Create the first user
              </Button>
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th style={{ width: 160 }}>Role</th>
                  <th style={{ width: 200 }}>Manages</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 220 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td>
                        <Link to={`/admin/users/${u.id}`} className="text-decoration-none fw-semibold" data-testid={`user-edit-link-${u.id}`}>
                          {u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                        </Link>
                        {isMe && <Badge bg="info" className="ms-2">You</Badge>}
                      </td>
                      <td><code style={{ fontSize: '0.82rem' }}>{u.email}</code></td>
                      <td style={{ fontSize: '0.85rem' }}>{u.phone || <span className="text-muted">—</span>}</td>
                      <td>
                        <Badge bg={u.role === 'admin' ? 'primary' : 'secondary'}>{ROLE_LABELS[u.role] || u.role}</Badge>
                      </td>
                      <td style={{ fontSize: '0.85rem' }} data-testid={`user-assigned-cell-${u.id}`}>
                        {u.role === 'campaign_manager'
                          ? (u.assigned_candidate
                              ? <Link to={`/candidates/${u.assigned_candidate.id}`} className="text-decoration-none">{u.assigned_candidate.full_name}</Link>
                              : <span className="text-muted">Not assigned</span>)
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        {u.is_active ? <Badge bg="success">Active</Badge> : <Badge bg="warning" text="dark">Disabled</Badge>}
                      </td>
                      <td className="text-end">
                        <Button as={Link} to={`/admin/users/${u.id}`} size="sm" variant="outline-secondary" className="me-1" data-testid={`user-edit-button-${u.id}`}>Edit</Button>
                        <Button size="sm" variant={u.is_active ? 'outline-warning' : 'outline-success'} className="me-1" onClick={() => toggleActive(u)} disabled={isMe} data-testid={`user-toggle-button-${u.id}`}>
                          {u.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setPendingDelete(u)} disabled={isMe} data-testid={`user-delete-button-${u.id}`}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: '0.85rem' }}>
            <span className="text-muted">Page {page} of {totalPages} · {total} users</span>
            <div className="d-flex gap-1">
              <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <Modal show={!!pendingDelete} onHide={() => !deleting && setPendingDelete(null)}>
        <Modal.Header closeButton><Modal.Title>Delete user?</Modal.Title></Modal.Header>
        <Modal.Body>
          Permanently delete <strong>{pendingDelete?.email}</strong>? They will be removed from any candidates they&apos;re currently assigned to. This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingDelete(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={doDelete} disabled={deleting} data-testid="users-delete-confirm-button">
            {deleting ? <><Spinner size="sm" animation="border" /> Deleting…</> : 'Delete user'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
